// src/hooks/useOverrides.js
import { useState, useEffect, useCallback, useRef } from 'react'
import { getOverrides, upsertOverride, deleteOverride } from '../db/index.js'
import { logEvent, getCurrentAdmin } from '../lib/agentLog.js'

// Several tabs mount their own useOverrides over the same table (Attendance and
// Schedule even share one page) — a save in one instance must reach the others
// without a page refresh, so every mutation is broadcast to all mounted hooks.
const overrideListeners = new Set()
function broadcastOverrideChange(msg) {
  overrideListeners.forEach(fn => fn(msg))
}

export function useOverrides(agents = [], dateFrom, dateTo) {
  const [overrides, setOverrides] = useState([])
  const [loading,   setLoading]   = useState(false)
  // ⚠️ a failed read is RETURNED (attendance audit 2026-10-21): it only reached the console, so the page drew base
  // schedules as if no one-off change existed. Generation-guarded like useAttendance, so an older range cannot land last.
  const [error,     setError]     = useState(null)
  const genRef = useRef(0)

  const load = useCallback(async () => {
    if (!dateFrom || !dateTo) return
    const gen = ++genRef.current
    setLoading(true)
    try {
      const data = await getOverrides({ dateFrom, dateTo })
      if (gen !== genRef.current) return
      setOverrides(data)
      setError(null)
    } catch (e) {
      console.error('Failed to load overrides', e)
      if (gen === genRef.current) setError(e?.message || 'error')
    } finally { if (gen === genRef.current) setLoading(false) }
  }, [dateFrom, dateTo])

  useEffect(() => { load() }, [load])

  // Apply changes made by OTHER useOverrides instances. Idempotent, so it is
  // safe even for the instance that originated the mutation.
  useEffect(() => {
    const apply = (msg) => {
      if (msg.type === 'upsert') {
        setOverrides(prev => {
          const filtered = prev.filter(r => !(r.agent_id === msg.record.agent_id && r.date === msg.record.date))
          return [...filtered, msg.record]
        })
      } else if (msg.type === 'remove') {
        setOverrides(prev => prev.filter(r => r.id !== msg.id))
      }
    }
    overrideListeners.add(apply)
    return () => overrideListeners.delete(apply)
  }, [])

  // keyed: { [agentId]: { [date]: override } }
  const byAgent = {}
  overrides.forEach(o => {
    if (!byAgent[o.agent_id]) byAgent[o.agent_id] = {}
    byAgent[o.agent_id][o.date] = o
  })

  const upsert = useCallback(async (record) => {
    // 🔴 THE AUTHOR IS WRITTEN ON THE ROW, not only into a log sentence (owner 2026-09-10).
    // `schedule_overrides.created_by` (text, in the schema since the table was created) was NULL
    // on all 51 rows: the identity was fetched two lines below this one for the log MESSAGE and
    // never put on the record. So "who set this shift?" could only be answered by text-matching
    // prose in another table — which is how Aleksandra's 05:10 was traced to lukab, and it is
    // not a question a database should need prose to answer.
    // ⚠️ It is an UPSERT keyed on (agent_id, date), so this records the LAST writer, not
    // strictly the creator. That is the honest reading of the column here and it is still worth
    // far more than null; `agent_logs` keeps the full sequence of who touched it when.
    // ⚠️ No backfill: the 51 existing rows stay NULL. A guessed author is worse than an admitted
    // gap — attribution starts now (the same rule the event log's own actor work followed).
    // 📌 A uuid link would be stronger than a login string (rename the login and old rows still
    // say `lukab` with nothing joining them). That needs a schema change; the text column is what
    // exists, and filling it is the fix available today.
    const saved = await upsertOverride({ ...record, created_by: getCurrentAdmin() })
    // Broadcast reaches every mounted instance, including this one.
    broadcastOverrideChange({ type: 'upsert', record: saved })

    // Log it
    const agent = agents.find(a => a.id === record.agent_id)
    if (agent) {
      const by   = getCurrentAdmin()
      const date = new Date(record.date + 'T12:00:00')
        .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })

      const slotsStr = record.slots?.length > 0
        ? record.slots.map(s => `${s.start}–${s.end} ${s.location}`).join(' + ')
        : 'OFF (day off)'

      logEvent(agent.id, agent.name, 'schedule',
        `One-off override on ${date} by ${by}: ${slotsStr}.` +
        (record.reason ? ` Reason: ${record.reason}.` : '')
      )
    }
    return saved
  }, [agents])

  const remove = useCallback(async (id) => {
    const rec   = overrides.find(r => r.id === id)
    const agent = rec && agents.find(a => a.id === rec.agent_id)
    if (agent && rec) {
      logEvent(agent.id, agent.name, 'schedule',
        `Override for ${rec.date} removed by ${getCurrentAdmin()}.`)
    }
    await deleteOverride(id)
    broadcastOverrideChange({ type: 'remove', id })
  }, [overrides, agents])

  return { overrides, byAgent, loading, error, reload: load, upsert, remove }
}
